<template>
  <div class="controls">
    <div class="controls__filter">
      <div
        aria-controls="filters-dropdown"
        class="btn -has-icon -dual upper"
        @click="toggleFilterDropdown"
      >
        <span class="iconfont-filter" />Topics<span class="iconfont-carrot" />
      </div>

      <div
        v-if="dropdownFilterActive"
        class="dropdown-overlay"
        @click.prevent="toggleFilterDropdown"
      />
      <div
        v-if="dropdownFilterActive"
        id="filters-dropdown"
        class="dropdown-menu-holder"
        @mouseleave="
          () => {
            !isMobileWidth() && toggleFilterDropdown()
          }
        "
      >
        <div class="dropdown-menu">
          <header class="header">
            <h4 class="heading -small">Filter</h4>
            <button class="hamburger -exed" @click.prevent="toggleFilterDropdown">
              <span class="inner">
                <i />
              </span>
            </button>
          </header>

          <ul>
            <li
              v-for="(cat, index) in categories"
              :key="index"
              :class="{ open: index === categoryIndex }"
            >
              <a
                :class="{ active: isCatActive(cat.title, index) || index === categoryIndex }"
                :href="cat.url + query"
                @mouseenter="
                  () => {
                    !isMobileWidth() && changeCategory(index)
                  }
                "
                @click.prevent="changeCategory(index, !isMobileWidth(), cat.url + query)"
                >{{ cat.title }}<span class="iconfont-carrot"
              /></a>
              <ul class="mobile-submenu">
                <li>
                  <a :href="cat.url + query">{{ cat.title }}</a>
                </li>
                <!-- eslint-disable-next-line -->
                <li v-for="subcat in subcategories[0]">
                  <a :class="{ active: isCatActive(subcat.title) }" :href="subcat.url + query">{{
                    subcat.title
                  }}</a>
                </li>
              </ul>
            </li>
          </ul>
          <div class="submenu">
            <!-- eslint-disable-next-line -->
            <ul v-for="subcatList in subcategories">
              <!-- eslint-disable-next-line -->
              <li v-for="subcat in subcatList">
                <a :class="{ active: isCatActive(subcat.title) }" :href="subcat.url + query">{{
                  subcat.title
                }}</a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div class="flex-right">
      <div class="discussion-action">
        <a href="/community/create" class="btn -main">Start a Discussion</a>
        <a href="/community/create" class="discussion-icon"><span class="iconfont-discussion" /></a>
      </div>

      <button class="btn -has-icon icon-only" aria-label="Search" @click="toggleSearchDropdown">
        <span aria-hidden="true" class="iconfont-search" />
      </button>

      <form
        v-show="searchDropdownActive"
        action="POST"
        class="search-form-small"
        @mouseleave="toggleSearchDropdown"
        @submit.prevent="onSearchSubmit"
      >
        <div class="inner">
          <label class="sr-only" for="search">Search</label>
          <input
            id="search"
            ref="searchInput"
            type="text"
            placeholder="Keyword Search"
            class="large-input"
          />
          <button type="submit" aria-label="Search">
            <span aria-hidden="true" class="iconfont-search" />
          </button>
        </div>
      </form>

      <div class="form__group -inline">
        <label for="sort">Sort</label>
        <div class="select-style">
          <form>
            <select id="sort" v-model="sortValue" name="sort">
              <option value="newest">Newest First</option>
              <option value="updated">Last Update</option>
              <option value="popular">Most Popular</option>
              <option value="oldest">Oldest First</option>
            </select>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, nextTick, watch } from 'vue'
import { useRoute } from 'vue-router'
import { bp } from '@/constants'
import type { Category } from '@/types/global-types'

/** PROPS **/
const props = withDefaults(
  defineProps<{
    categories: Category[]
    activeCategory: Category | null | undefined
    activeSubcategory: Category | null | undefined
  }>(),
  {
    activeCategory: null,
    activeSubcategory: null,
  },
)

/** STATE **/
const route = useRoute()
const categoryIndex = ref<number>(-1)
const dropdownFilterActive = ref<boolean>(false)
const searchDropdownActive = ref<boolean>(false)
const sortValue = ref(route.query.sort || 'newest')
const searchInput = ref<HTMLInputElement | null>(null)

/** COMPUTED **/
const subcategories = computed(() => {
  if (categoryIndex.value < 0) {
    return []
  }

  const index = categoryIndex.value
  const subcats = props.categories[index].subcategories || []

  if (subcats.length > props.categories.length) {
    const half = Math.ceil(subcats.length / 2)
    return [subcats.slice(0, half), subcats.slice(half)]
  } else {
    return [subcats]
  }
})

const query = computed(() => {
  const sort = route.query.sort
  const keywords = route.query.keywords
  if (keywords && sort) {
    return `?sort=${sort}&keywords=${keywords}`
  } else if (sort) {
    return `?sort=${sort}`
  } else if (keywords) {
    return `?keywords=${keywords}`
  } else {
    return ''
  }
})

/** WATCHERS **/
watch(sortValue, (newSort, prevSort) => {
  if (newSort !== prevSort) {
    handleSort(`${newSort}`)
  }
})

/** METHODS **/
const isMobileWidth = () => {
  const winWidth = window.innerWidth
  return bp.small >= winWidth
}

const changeCategory = (index: number, redirect: boolean = false, url: string = '') => {
  if (!redirect) {
    categoryIndex.value = categoryIndex.value === index ? -1 : index
  } else {
    window.location.href = url
  }
}

const isCatActive = (title: string, index: number | null = null) => {
  if (!props.activeCategory && !props.activeSubcategory) {
    return
  }

  title = title.replace(/&/g, '&amp;')

  const matchesActiveCatTitle = props.activeCategory && title === props.activeCategory.title
  const matchesActiveSubcatTitle =
    props.activeSubcategory && title === props.activeSubcategory.title

  const isActive = matchesActiveCatTitle || matchesActiveSubcatTitle

  if (isActive && index && !isMobileWidth()) {
    categoryIndex.value === -1 && changeCategory(index)
  }

  return isActive
}

const toggleFilterDropdown = () => {
  dropdownFilterActive.value = !dropdownFilterActive.value
  !dropdownFilterActive.value && changeCategory(-1)
}

const toggleSearchDropdown = () => {
  searchDropdownActive.value = !searchDropdownActive.value
  if (searchDropdownActive.value) {
    nextTick(() => {
      searchInput.value && searchInput.value.focus()
    })
  }
}

const handleSort = (value: string) => {
  const query = route.query
  if (query.keywords) {
    communitySort(value, `${route.path}?keywords=${query.keywords}`, true)
  } else {
    communitySort(value, route.path)
  }
}

const onSearchSubmit = () => {
  const query = route.query
  const searchValue = searchInput.value?.value || ''
  if (query.sort) {
    communitySearch(searchValue, `${route.path}?sort=${query.sort}`, true)
  } else {
    communitySearch(searchValue, route.path)
  }
}

const communitySort = (val: string, path: string = '/community', hasQuery: boolean = false) => {
  if (!hasQuery) {
    window.location.href = `${path}?sort=${encodeURIComponent(val)}`
  } else {
    window.location.href = `${path}&sort=${encodeURIComponent(val)}`
  }
}

const communitySearch = (val: string, path: string = '/community', hasQuery: boolean = false) => {
  if (!hasQuery) {
    window.location.href = `${path}?keywords=${encodeURIComponent(val)}`
  } else {
    window.location.href = `${path}&keywords=${encodeURIComponent(val)}`
  }
}
</script>
